import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getUserMenuAndPermissions } from '../services/menuApi';
import { getCapabilityRequestData } from '../services/capabilitiesApi';
import { loadClientPortalSnapshotSWR } from '../services/clientProjectData';
import { invalidateClientDataCache } from '../utils/clientProjectDataCache';
import { getProjectSetupRequests } from '../services/projectSetupRequestApi';
import { useAuth } from '../auth/AuthContext';
import {
    isApproverUser,
    isClientPortalUser,
    isSuperAdminUser,
} from '../auth/roleUtils';
import { hideSuperAdminMasterCatalogMenu } from '../utils/superAdminSidebarMenu';
import { 
    clearAllMenuCaches,
    clearMenuCacheForRole,
    isCacheStale,
    getCacheWithTimestamp,
    setCacheWithTimestamp
} from './menuCacheUtils';
import {
    resolveMenuPath,
    withClientImportExportMenu,
    withSmeProjectReportsMenu,
} from '../utils/menuRouteUtils';
import {
    CHAT_READ_STATE_CHANGED,
    hasUnreadChatNotification,
} from '../utils/chatReadState';
import {
    REPORT_READ_STATE_CHANGED,
    areReportsUnread,
    getLatestClientReportDate,
    getClientVisibleReports,
} from '../utils/reportReadState';
import { SA_PENDING_APPROVALS_CHANGED } from '../utils/saPendingApprovals';
import {
    CLIENT_NAV_READ_STATE_CHANGED,
    isClientNavPageViewed,
} from '../utils/clientNavReadState';

const MenuContext = createContext();

const finalizeMenuItems = (items, user) => {
    const withSmeReports = withSmeProjectReportsMenu(items);
    const withImportExport = withClientImportExportMenu(withSmeReports);
    if (!isSuperAdminUser(user)) {
        return withImportExport;
    }
    return hideSuperAdminMasterCatalogMenu(withImportExport);
};

const hasAllowedPermission = (item) =>
    Array.isArray(item?.permissions) &&
    item.permissions.some((p) => Number(p.isAllowed) === 1);

/**
 * Recursively keep menus the user can open, including category/parent folders
 * that only exist to hold allowed children (e.g. Master Data → Value Estimation Setup).
 */
const filterMenuTree = (menu) => {
    if (!menu || !hasAllowedPermission(menu)) {
        return null;
    }

    const childMenus = Array.isArray(menu.subMenus)
        ? menu.subMenus.map(filterMenuTree).filter(Boolean)
        : [];

    const hasRoute = resolveMenuPath(menu) !== null;
    const isFolder = childMenus.length > 0 || Boolean(menu.isCategory);

    if (!hasRoute && !isFolder) {
        return null;
    }

    return {
        ...menu,
        subMenus: childMenus,
    };
};

export const useMenu = () => {
    const context = useContext(MenuContext);
    if (!context) {
        throw new Error('useMenu must be used within a MenuProvider');
    }
    return context;
};

export const MenuProvider = ({ children }) => {
    const { user } = useAuth();
    const [menuItems, setMenuItems] = useState([]);
    const [loadingMenu, setLoadingMenu] = useState(true);
    const [chatCount, setChatCount] = useState(0);
    const [hasUnreadChat, setHasUnreadChat] = useState(false);
    const [hasUnreadReport, setHasUnreadReport] = useState(false);
    const [clientHasSelectedCapabilities, setClientHasSelectedCapabilities] = useState(false);
    const [clientHasSubmittedReports, setClientHasSubmittedReports] = useState(false);
    const [hasNewAssessmentNav, setHasNewAssessmentNav] = useState(false);
    const [hasUnopenedReportsNav, setHasUnopenedReportsNav] = useState(false);
    const [pendingCapabilityCount, setPendingCapabilityCount] = useState(0);
    const [pendingProjectSetupCount, setPendingProjectSetupCount] = useState(0);
    const [menuError, setMenuError] = useState(null);
    const [chatError, setChatError] = useState(null);
    const [authData, setAuthData] = useState(null);
    const chatHistoryRef = useRef([]);
    const latestReportDateRef = useRef(null);
    const projectIdRef = useRef(null);
    const previousUserIdRef = useRef<number | string | null>(null);
    const userRef = useRef(user);

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    const canReviewApprovals = useMemo(
        () => isApproverUser(user ?? authData),
        [user, authData]
    );

    useEffect(() => {
        if (!user) {
            setAuthData(null);
            return;
        }

        setAuthData({
            userId: user.userId,
            roleId: user.roleId,
            roleName: user.roleName,
            userType: user.userType,
            state: user.state,
            projectDetails: user.projectDetails,
        });
    }, [user]);

    const roleId = useMemo(() => {
        try {
            if (!authData) return null;

            const resolvedRoleId =
                authData.roleId ?? authData.userRoleId ?? authData.userTypeId ?? null;

            if (resolvedRoleId != null) {
                return resolvedRoleId;
            }

            console.warn(
                'MenuContext: user has no roleId from API; menu permissions may be incorrect.',
                authData.roleName
            );
            return null;
        } catch (error) {
            console.error('Error parsing profile data:', error);
            return null;
        }
    }, [authData]);

    // Get chat count cache key - Fixed to react to changes
    const chatCountCacheKey = useMemo(() => {
        try {
            const selectedProjectId = localStorage.getItem('projectId');
            if (!selectedProjectId || !user?.userId) return null;
            return `chat_${user.userId}_${selectedProjectId}`;
        } catch (error) {
            return null;
        }
    }, [user, authData]);

    const isClientPortal = useMemo(() => isClientPortalUser(user ?? authData), [user, authData]);

    // Fetch menu data with caching
    const fetchMenu = useCallback(async () => {
        if (!roleId) {
            setMenuItems([]);
            setLoadingMenu(false);
            return;
        }

        // Check cache first
        const cacheKey = `menu_${roleId}`;
        const cachedData = getCacheWithTimestamp(cacheKey);
        
        if (cachedData && !isCacheStale(cacheKey)) {
            setMenuItems(finalizeMenuItems(cachedData.data, userRef.current));
            setLoadingMenu(false);
            return;
        }

        setLoadingMenu(true);
        setMenuError(null);
        
        try {
            const res = await getUserMenuAndPermissions(roleId);
            
            // Filter by permissions and drop folder-only items with no route
            const allowed = Array.isArray(res.data)
                ? res.data
                    .map((menu) => filterMenuTree(menu))
                    .filter(Boolean)
                : [];
            
            // Cache the filtered menu data
            setCacheWithTimestamp(cacheKey, allowed);
            setMenuItems(finalizeMenuItems(allowed, userRef.current));
        } catch (error) {
            setMenuError(error.message);
            console.error('Menu API error:', error);
        } finally {
            setLoadingMenu(false);
        }
    }, [roleId]);

    // Clear cached menus only when switching accounts — not on session restore / refresh.
    useEffect(() => {
        const nextUserId = user?.userId ?? null;
        if (nextUserId == null) {
            previousUserIdRef.current = null;
            return;
        }

        const previousUserId = previousUserIdRef.current;
        if (previousUserId != null && previousUserId !== nextUserId) {
            clearAllMenuCaches();
        }
        previousUserIdRef.current = nextUserId;
    }, [user?.userId]);

    // Fetch menu when roleId becomes available
    useEffect(() => {
        if (roleId) {
            fetchMenu();
        } else {
            setMenuItems([]);
            setLoadingMenu(false);
        }
    }, [roleId, fetchMenu]);

    const recomputeUnreadFromHistory = useCallback(() => {
        const selectedProjectId =
            projectIdRef.current || localStorage.getItem('projectId');

        if (!user?.userId || !selectedProjectId) {
            setHasUnreadChat(false);
            setHasUnreadReport(false);
            return;
        }

        setHasUnreadChat(
            hasUnreadChatNotification(
                chatHistoryRef.current,
                user.userId,
                selectedProjectId,
                'client'
            )
        );
        setHasUnreadReport(
            areReportsUnread(
                user.userId,
                selectedProjectId,
                latestReportDateRef.current
            )
        );
    }, [user]);

    const recomputeClientNavPageAlerts = useCallback(() => {
        if (!isClientPortal) {
            setHasNewAssessmentNav(false);
            setHasUnopenedReportsNav(false);
            return;
        }

        const activeUser = userRef.current;
        const selectedProjectId =
            projectIdRef.current || localStorage.getItem('projectId');

        if (!activeUser?.userId || !selectedProjectId) {
            setHasNewAssessmentNav(false);
            setHasUnopenedReportsNav(false);
            return;
        }

        const assessmentUnlocked = clientHasSelectedCapabilities;
        const reportsUnlocked = clientHasSubmittedReports;

        setHasNewAssessmentNav(
            assessmentUnlocked &&
                !isClientNavPageViewed(
                    'assessment',
                    activeUser.userId,
                    selectedProjectId
                )
        );
        setHasUnopenedReportsNav(
            reportsUnlocked &&
                !isClientNavPageViewed(
                    'reports',
                    activeUser.userId,
                    selectedProjectId
                )
        );
    }, [isClientPortal, clientHasSelectedCapabilities, clientHasSubmittedReports]);

    // Fetch chat count + unread indicators for client sidebar / dashboard
    const fetchChatCount = useCallback(async () => {
        if (!isClientPortal || !chatCountCacheKey) return;

        try {
            const selectedProjectId = localStorage.getItem('projectId');
            const activeUser = userRef.current;

            if (!activeUser?.userId || !selectedProjectId) {
                setChatCount(0);
                setHasUnreadChat(false);
                setHasUnreadReport(false);
                setClientHasSelectedCapabilities(false);
                setClientHasSubmittedReports(false);
                setHasNewAssessmentNav(false);
                setHasUnopenedReportsNav(false);
                chatHistoryRef.current = [];
                latestReportDateRef.current = null;
                projectIdRef.current = null;
                return;
            }

            projectIdRef.current = selectedProjectId;

            const payload = {
                userId: activeUser.userId,
                projectId: selectedProjectId,
            };

            const cachedData = getCacheWithTimestamp(chatCountCacheKey);
            const cacheFresh = cachedData && !isCacheStale(chatCountCacheKey);
            if (cacheFresh) {
                setChatCount(cachedData.data);
            }

            const applySnapshot = (snapshot: {
                chatHistory: unknown[];
                reports: unknown[];
                selectedCapabilitiesRes: { data?: { selectedCapabilities?: unknown[] } } | null;
                chatCount: number;
            }) => {
                setCacheWithTimestamp(chatCountCacheKey, snapshot.chatCount);
                setChatCount(snapshot.chatCount);

                chatHistoryRef.current = snapshot.chatHistory;
                setHasUnreadChat(
                    hasUnreadChatNotification(
                        chatHistoryRef.current,
                        activeUser.userId,
                        selectedProjectId,
                        'client'
                    )
                );

                latestReportDateRef.current = getLatestClientReportDate(snapshot.reports);
                setHasUnreadReport(
                    areReportsUnread(
                        activeUser.userId,
                        selectedProjectId,
                        latestReportDateRef.current
                    )
                );

                const selectedCaps = snapshot.selectedCapabilitiesRes?.data?.selectedCapabilities;
                setClientHasSelectedCapabilities(
                    Array.isArray(selectedCaps) && selectedCaps.length > 0
                );
                setClientHasSubmittedReports(
                    getClientVisibleReports(snapshot.reports).length > 0
                );
            };

            const { data: snapshot, refresh } = await loadClientPortalSnapshotSWR({
                userId: activeUser.userId,
                projectId: selectedProjectId,
            });

            if (snapshot) {
                applySnapshot(snapshot);
            }

            if (refresh) {
                refresh
                    .then((fresh) => {
                        applySnapshot(fresh);
                        setChatError(null);
                    })
                    .catch((error) => {
                        if (!snapshot) {
                            setChatError(error.message);
                        }
                    });
            } else {
                setChatError(null);
            }
        } catch (error) {
            setChatError(error.message);
            console.error("Error fetching chat count:", error);
        }
    }, [chatCountCacheKey, isClientPortal]);

    // Refresh menu data (useful for admin changes)
    const refreshMenu = useCallback(() => {
        clearMenuCacheForRole(roleId);
        fetchMenu();
    }, [fetchMenu, roleId]);

    // Refresh chat count
    const refreshChatCount = useCallback(() => {
        // Clear cache and fetch fresh data
        if (chatCountCacheKey) {
            localStorage.removeItem(chatCountCacheKey);
        }
        const selectedProjectId = localStorage.getItem('projectId');
        const activeUserId = userRef.current?.userId;
        if (activeUserId && selectedProjectId) {
            invalidateClientDataCache({
                userId: activeUserId,
                projectId: selectedProjectId,
                tags: ['portalSnapshot', 'chat', 'reports', 'selectedCapabilities'],
            });
        }
        fetchChatCount();
    }, [fetchChatCount, chatCountCacheKey]);

    const clearPendingApprovals = useCallback(() => {
        setPendingCapabilityCount(0);
        setPendingProjectSetupCount(0);
    }, []);

    // Pending capability / project-setup requests for SuperAdmin + Admin
    const fetchPendingApprovals = useCallback(async () => {
        if (!canReviewApprovals) {
            clearPendingApprovals();
            return;
        }

        try {
            const [capResult, setupResult] = await Promise.all([
                getCapabilityRequestData('', 'Pending', 1, 1).catch(() => ({
                    totalRecords: 0,
                })),
                getProjectSetupRequests('', 'Pending', 1, 1).catch(() => ({
                    totalRecords: 0,
                })),
            ]);

            setPendingCapabilityCount(Number(capResult?.totalRecords) || 0);
            setPendingProjectSetupCount(Number(setupResult?.totalRecords) || 0);
        } catch (error) {
            console.error('Error fetching pending approval counts:', error);
        }
    }, [canReviewApprovals, clearPendingApprovals]);

    const refreshPendingApprovals = useCallback(() => {
        fetchPendingApprovals();
    }, [fetchPendingApprovals]);

    // Set up event listeners for cache clearing
    useEffect(() => {
        const handleClearMenuCache = () => {
            clearAllMenuCaches();
            setMenuItems([]);
            setChatCount(0);
            setHasUnreadChat(false);
            setHasUnreadReport(false);
            clearPendingApprovals();
            fetchMenu();
            fetchChatCount();
            fetchPendingApprovals();
        };

        const handleProjectChanged = () => {
            setChatCount(0);
            setHasUnreadChat(false);
            setHasUnreadReport(false);
            setClientHasSelectedCapabilities(false);
            setClientHasSubmittedReports(false);
            chatHistoryRef.current = [];
            latestReportDateRef.current = null;
            fetchChatCount();
        };

        const handleAdminMenuChanged = () => {
            clearAllMenuCaches();
            setMenuItems([]);
            fetchMenu();
        };

        const handleUserLogout = () => {
            setMenuItems([]);
            setChatCount(0);
            setHasUnreadChat(false);
            setHasUnreadReport(false);
            setClientHasSelectedCapabilities(false);
            setClientHasSubmittedReports(false);
            clearPendingApprovals();
            chatHistoryRef.current = [];
            latestReportDateRef.current = null;
            setMenuError(null);
            setChatError(null);
        };

        const handleChatReadStateChanged = () => {
            recomputeUnreadFromHistory();
        };

        const handleReportReadStateChanged = () => {
            recomputeUnreadFromHistory();
        };

        const handleClientNavReadStateChanged = () => {
            recomputeClientNavPageAlerts();
        };

        const handleSaPendingApprovalsChanged = () => {
            fetchPendingApprovals();
        };

        // Add event listeners
        window.addEventListener('clearMenuCache', handleClearMenuCache);
        window.addEventListener('projectChanged', handleProjectChanged);
        window.addEventListener('adminMenuChanged', handleAdminMenuChanged);
        window.addEventListener('userLogout', handleUserLogout);
        window.addEventListener(CHAT_READ_STATE_CHANGED, handleChatReadStateChanged);
        window.addEventListener(REPORT_READ_STATE_CHANGED, handleReportReadStateChanged);
        window.addEventListener(
            CLIENT_NAV_READ_STATE_CHANGED,
            handleClientNavReadStateChanged
        );
        window.addEventListener(SA_PENDING_APPROVALS_CHANGED, handleSaPendingApprovalsChanged);

        // Cleanup event listeners
        return () => {
            window.removeEventListener('clearMenuCache', handleClearMenuCache);
            window.removeEventListener('projectChanged', handleProjectChanged);
            window.removeEventListener('adminMenuChanged', handleAdminMenuChanged);
            window.removeEventListener('userLogout', handleUserLogout);
            window.removeEventListener(CHAT_READ_STATE_CHANGED, handleChatReadStateChanged);
            window.removeEventListener(REPORT_READ_STATE_CHANGED, handleReportReadStateChanged);
            window.removeEventListener(
                CLIENT_NAV_READ_STATE_CHANGED,
                handleClientNavReadStateChanged
            );
            window.removeEventListener(SA_PENDING_APPROVALS_CHANGED, handleSaPendingApprovalsChanged);
        };
    }, [
        fetchMenu,
        fetchChatCount,
        fetchPendingApprovals,
        clearPendingApprovals,
        recomputeUnreadFromHistory,
        recomputeClientNavPageAlerts,
    ]);

    // Client portal: chat/report sidebar badges
    useEffect(() => {
        if (!isClientPortal) {
            setChatCount(0);
            setHasUnreadChat(false);
            setHasUnreadReport(false);
            setHasNewAssessmentNav(false);
            setHasUnopenedReportsNav(false);
            return;
        }
        fetchChatCount();
    }, [fetchChatCount, isClientPortal]);

    useEffect(() => {
        recomputeClientNavPageAlerts();
    }, [recomputeClientNavPageAlerts]);

    useEffect(() => {
        if (!isClientPortal) return undefined;

        const interval = setInterval(() => {
            fetchChatCount();
        }, 60000);

        return () => clearInterval(interval);
    }, [fetchChatCount, isClientPortal]);

    useEffect(() => {
        fetchPendingApprovals();
    }, [fetchPendingApprovals]);

    useEffect(() => {
        if (!canReviewApprovals) return undefined;

        const interval = setInterval(() => {
            fetchPendingApprovals();
        }, 30000);

        return () => clearInterval(interval);
    }, [canReviewApprovals, fetchPendingApprovals]);

    const hasPendingCapabilityApprovals = pendingCapabilityCount > 0;
    const hasPendingProjectSetup = pendingProjectSetupCount > 0;

    const clientAssessmentNavLocked =
        isClientPortal && !clientHasSelectedCapabilities;
    const clientReportsNavLocked = isClientPortal && !clientHasSubmittedReports;

    const value = {
        menuItems,
        loadingMenu,
        chatCount,
        hasUnreadChat,
        hasUnreadReport,
        hasNewAssessmentNav,
        hasUnopenedReportsNav,
        clientAssessmentNavLocked,
        clientReportsNavLocked,
        hasPendingCapabilityApprovals,
        hasPendingProjectSetup,
        pendingCapabilityCount,
        pendingProjectSetupCount,
        menuError,
        chatError,
        refreshMenu,
        refreshChatCount,
        refreshPendingApprovals,
        roleId
    };

    return (
        <MenuContext.Provider value={value}>
            {children}
        </MenuContext.Provider>
    );
};
